import React, { useEffect, useState, Suspense } from 'react';
import { Switch, useLocation } from 'react-router-dom';

import { ApolloRoute } from 'routes/ApolloRoutes';
import { AdminLayout, Loader } from '@safelyq/base-feature';
import useGaTracker from 'hooks/useGaTracker';

// Lazy load components
const ViewHome = React.lazy(() => import('@safelyq/business-admin').then(module => ({ default: module.ViewHome })));
const AdminLandingPage = React.lazy(() => import('@safelyq/business-admin').then(module => ({ default: module.AdminLandingPage })));
const ManageLogs = React.lazy(() => import('@safelyq/appointment-logs-feature').then(module => ({ default: module.ManageLogs })));
const ManageNotifications = React.lazy(() => import('@safelyq/admin-notifications').then(module => ({ default: module.ManageNotifications })));
const ManageReports = React.lazy(() => import('@safelyq/reports-feature').then(module => ({ default: module.ManageReports })));
const ManageAdminMessages = React.lazy(() => import('@safelyq/admin-messages-feature').then(module => ({ default: module.ManageAdminMessages })));
const ManageAppointment = React.lazy(() => import('@safelyq/admin-appointment-feature').then(module => ({ default: module.ManageAppointment })));
const AddAppointment = React.lazy(() => import('@safelyq/admin-appointment-feature').then(module => ({ default: module.AddAppointment })));
const AddCampaign = React.lazy(() => import('@safelyq/admin-campaign-feature').then(module => ({ default: module.AddCampaign })));
const ManageCampaign = React.lazy(() => import('@safelyq/admin-campaign-feature').then(module => ({ default: module.ManageCampaign })));
const ManageFiles = React.lazy(() => import('@safelyq/admin-campaign-feature').then(module => ({ default: module.ManageFiles })));
const CampaignDeliveryDetails = React.lazy(() => import('@safelyq/admin-campaign-feature').then(module => ({ default: module.CampaignDeliveryDetails })));
const ManageAdminSettings = React.lazy(() => import('@safelyq/account-feature').then(module => ({ default: module.ManageAdminSettings })));
const ManageBusiness = React.lazy(() => import('@safelyq/manage-business').then(module => ({ default: module.ManageBusiness })));
const ManageReviews = React.lazy(() => import('@safelyq/manage-business').then(module => ({ default: module.ManageReviews })));
const CreateOrganization = React.lazy(() => import('@safelyq/manage-business').then(module => ({ default: module.CreateOrganization })));
const ManageReserveWithGoogle = React.lazy(() => import('@safelyq/manage-business').then(module => ({ default: module.ManageReserveWithGoogle })));
const ManageConsole = React.lazy(() => import('@safelyq/manage-business').then(module => ({ default: module.ManageConsole })));
const ManagePromotions = React.lazy(() => import('@safelyq/admin-promotions-feature').then(module => ({ default: module.ManagePromotions })));
const ManageMembers = React.lazy(() => import('@safelyq/manage-members').then(module => ({ default: module.ManageMembers })));
const ManageOrders = React.lazy(() => import('features/admin-orders').then(module => ({ default: module.ManageOrders })));
const ViewForms = React.lazy(() => import('features/admin-forms-feature/ViewForms'));
const CreateForm = React.lazy(() => import('features/admin-forms-feature/CreateForm'));
const ManageSubscription = React.lazy(() => import('./../../features/manage-business/src/routes/ManageSubscription/index'));
const StripeAccount = React.lazy(() => import('components/AdminStripeAccount'));
const ViewExternalForms = React.lazy(() => import('features/admin-forms-feature/ViewExternalForms'));
const CreateExternalForm = React.lazy(() => import('features/admin-forms-feature/CreateExternalForm'));
const SalesPage = React.lazy(() => import('features/admin-inventory/SalesPage'));
const NotFound = React.lazy(() => import('components/NotFound'));

export const AdminRoutes = ({ profile }) => {
  const { pathname: tempPath } = useLocation();
  const pathname = tempPath.toLowerCase();

  const [found, setFound] = useState(true);
  useGaTracker();
  useEffect(() => {
    if (pathname.includes('/admin/businesses/') && pathname !== '/admin/businesses/optout') {
      setFound(false);
    } else {
      setFound(true);
    }
  }, [pathname]);



  const isAllowSideBar =
    pathname !== '/' &&
    pathname !== '/admin' &&
    pathname !== '/admin/' &&
    !pathname.startsWith('/admin/businesses') &&
    pathname !== '/admin/notifications' &&
    pathname !== '/admin/account/setting' &&
    pathname !== '/admin/account/notifications' &&
    pathname !== '/admin/account/verify-phone' &&
    pathname !== '/admin/create-organization' &&
    pathname !== '/admin/manage-business';

  const isAllowFullScreen = pathname.includes('/admin/dashboard') || pathname.includes('/reports-summary');
  const isAllowNotifications =
    pathname.includes('/dashboard') ||
    pathname.includes('/appointments') ||
    pathname.includes('/create-appointment') ||
    pathname.includes('/messages') ||
    pathname.includes('/manage-reviews') ||
    pathname.includes('/reports-summary') ||
    pathname.includes('/admin/notifications') ||
    pathname.includes('/promotions') ||
    pathname.includes('/campaign') ||
    pathname.includes('/create-campaign') ||
    pathname.includes('/manage-users') ||
    pathname.includes('/create-form') ||
    pathname.includes('/forms') ||
    pathname.includes('/view-campaign') ||
    pathname.includes('/manage-files') ||
    pathname.includes('/manage-console') ||
    pathname.includes('/coupons') ||
    pathname.includes('/reserve-with-google') ||
    pathname.includes('/pricing') ||
    pathname.includes('/inventory') ||
    pathname.includes('/orders') ||
    pathname.includes('/manage-business');


  const pathId = String(pathname).substring(pathname.lastIndexOf('/') + 1);
  const businessId = Number(pathId) ? parseInt(pathId) : 0;

  return (
    !found ? (
      <Suspense fallback={<Loader isFullScreen />}>
        <NotFound />
      </Suspense>
    ) : (

      <Switch>
        <AdminLayout
          isAllowSideBar={isAllowSideBar}
          isAllowFullScreen={isAllowFullScreen}
          isAllowNotifications={isAllowNotifications}
          businessId={businessId}
          {...profile}
        >
          {/* Switch -> AdminLayout -> Suspense -> ApolloRoute -> Page */}
          {/* Boundary sits below the layout so only the page suspends; one hoisted AdminLayout covers all routes, so no wrapper needed. */}
          <Suspense fallback={<Loader isFullScreen />}>
            {/* Supports multiple paths */}
            <ApolloRoute exact path={['/', '/admin']} isNavbarHide={true} profile={profile} component={AdminLandingPage} />

            <ApolloRoute secure exact path='/admin/businesses/:reserve?' isNavbarHide={true} profile={profile}
              component={() =>
                <ViewHome />
              }
            />
            <ApolloRoute secure exact isNavbarHide={true} path='/admin/dashboard/:businessId' profile={profile}
              component={() =>
                <ManageLogs />
              }
            />
            <ApolloRoute secure exact isNavbarHide={true} path='/admin/appointments/:businessId' profile={profile}
              component={() =>
                <ManageAppointment />
              }
            />
            <ApolloRoute secure exact isNavbarHide={true} path='/admin/create-appointment/:businessId' profile={profile}
              component={() =>

                <AddAppointment />
              }
            />
            <ApolloRoute secure exact isNavbarHide={true} path='/admin/view-campaign/:businessId' profile={profile}
              component={() =>
                <ManageCampaign />
              }
            />
            <ApolloRoute secure exact isNavbarHide={true} path='/admin/create-campaign/:businessId' profile={profile}
              component={AddCampaign}
            />
            <ApolloRoute secure exact isNavbarHide={true} path='/admin/manage-files/:businessId' profile={profile}
              component={() =>
                <ManageFiles />
              }
            />
            <ApolloRoute secure exact isNavbarHide={true} path='/admin/campaign-delivery-details/:businessId' profile={profile}
              component={() =>
                <CampaignDeliveryDetails />
              }
            />
            <ApolloRoute secure exact isNavbarHide={true} path='/admin/notifications/:businessId' profile={profile}
              component={() =>
                <ManageNotifications />
              }
            />
            <ApolloRoute secure exact isNavbarHide={true} path='/admin/reports-summary/:businessId' profile={profile}
              component={() =>
                <ManageReports />
              }
            />
            <ApolloRoute secure exact isNavbarHide={true} path='/admin/messages/:businessId' profile={profile}
              component={() =>
                <ManageAdminMessages />
              }
            />
            <ApolloRoute secure exact isNavbarHide={true} path='/admin/inventory/:businessId' profile={profile}
              component={() =>
                <SalesPage />
              }
            />
            <ApolloRoute secure exact isNavbarHide={true} path='/admin/orders/:businessId' profile={profile}
              component={() =>
                <ManageOrders />
              }
            />
            <ApolloRoute secure exact isNavbarHide={true} path='/admin/manage-business/holidays/:businessId' profile={profile}
              component={() =>
                <ManageBusiness />
              }
            />
            <ApolloRoute secure exact isNavbarHide={true} path='/admin/manage-reviews/:businessId' profile={profile}
              component={() =>
                <ManageReviews />
              }
            />
            <ApolloRoute secure exact isNavbarHide={true} path='/admin/manage-business/:id?' profile={profile}
              component={() =>
                <ManageBusiness />
              }
            />
            <ApolloRoute secure exact isNavbarHide={true} path='/admin/manage-console/:id?' profile={profile}
              component={() =>
                <ManageConsole />
              }
            />
            <ApolloRoute secure exact isNavbarHide={true} path='/admin/reserve-with-google/:id' profile={profile}
              component={() =>
                <ManageReserveWithGoogle />
              }
            />
            <ApolloRoute secure exact isNavbarHide={true} path='/admin/pricing/:id' profile={profile}
              component={() =>
                <ManageSubscription />
              }
            />
            <ApolloRoute secure exact isNavbarHide={true} path='/admin/payment/:id' profile={profile}
              component={() =>
                <StripeAccount />
              }
            />
            <ApolloRoute secure exact isNavbarHide={true} path='/admin/coupons/:id?' profile={profile}
              component={() =>
                <ManagePromotions />
              }
            />
            <ApolloRoute secure exact isNavbarHide={true} path='/admin/create-organization' profile={profile}
              component={() =>
                <CreateOrganization />
              }
            />
            <ApolloRoute secure exact isNavbarHide={true} path='/admin/account' profile={profile}
              component={() =>
                <ManageAdminSettings />
              }
            />
            <ApolloRoute secure exact isNavbarHide={true} path='/admin/account/setting' profile={profile}
              component={() =>
                <ManageAdminSettings />
              }
            />
            <ApolloRoute secure exact isNavbarHide={true} path='/admin/account/notifications' profile={profile}
              component={() =>
                <ManageAdminSettings />
              }
            />
            <ApolloRoute secure exact isNavbarHide={true} path='/admin/account/verify-phone' profile={profile}
              component={() =>
                <ManageAdminSettings />
              }
            />
            <ApolloRoute secure exact isNavbarHide={true} path='/admin/manage-users/:id' profile={profile}
              component={() =>
                <ManageMembers />
              }
            />
            <ApolloRoute secure exact isNavbarHide={true} path='/admin/forms/:businessId' profile={profile}
              component={() =>
                <ViewForms />
              }
            />
            <ApolloRoute secure exact isNavbarHide={true} path='/admin/create-form/:businessId' profile={profile}
              component={() =>
                <CreateForm />
              }
            />
            <ApolloRoute secure exact isNavbarHide={true} path='/admin/external_form/:businessId' profile={profile}
              component={() =>
                <ViewExternalForms />
              }
            />
            <ApolloRoute secure exact isNavbarHide={true} path='/admin/create_external_form/:businessId' profile={profile}
              component={() =>
                <CreateExternalForm />
              }
            />
            {/* <Route path='*' render={(props) => <NotFound />} /> */}
          </Suspense>
        </AdminLayout>
      </Switch>
    )
  )
}

